import { useState } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { GET_PENDING_WITHDRAWALS } from "../api/review";
import { notify } from "../../../../utils/toast";

const WithdrawalsList = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState({
    userId: null as number | null,
    minAmount: null as number | null,
    maxAmount: null as number | null,
  });

  const { data, loading, error, refetch } = useQuery(GET_PENDING_WITHDRAWALS, {
    variables: {
      filter: {
        ...(filter.userId && { userId: filter.userId }),
        ...(filter.minAmount && { minAmount: filter.minAmount }),
        ...(filter.maxAmount && { maxAmount: filter.maxAmount }),
      },
    },
    fetchPolicy: "network-only",
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${label}이(가) 복사되었습니다.`);
    } catch (err) {
      notify("복사에 실패했습니다.");
    }
  };

  if (loading) return <div className="text-center py-8">로딩 중...</div>;
  if (error) return <div className="text-center py-8 text-red-600">에러: {error.message}</div>;

  const withdrawals = data?.missionAdminPendingWithdrawals || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">출금 요청 검수</h1>
      </div>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              유저 ID
            </label>
            <input
              type="number"
              value={filter.userId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  userId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="유저 ID 입력"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              최소 금액
            </label>
            <input
              type="number"
              value={filter.minAmount || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  minAmount: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="최소 금액 입력"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              최대 금액
            </label>
            <input
              type="number"
              value={filter.maxAmount || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  maxAmount: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="최대 금액 입력"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => refetch()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              검색
            </button>
          </div>
        </div>
      </div>

      {/* 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                유저
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                요청 금액
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                계좌 정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                요청 일시
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {withdrawals.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  대기 중인 출금 요청이 없습니다.
                </td>
              </tr>
            ) : (
              withdrawals.map((withdrawal: any) => (
                <tr
                  key={withdrawal.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() =>
                    navigate(`/ops/mission/reviews/withdrawals/${withdrawal.id}`)
                  }
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {withdrawal.user.name || `유저 ${withdrawal.user.id}`}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {withdrawal.user.id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-900">
                        {withdrawal.amountRequested.toLocaleString()}원
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            withdrawal.amountRequested.toString(),
                            "요청 금액"
                          );
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="복사"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-sm text-gray-900">
                          {withdrawal.bankName} {withdrawal.accountNumber || withdrawal.accountNumberMasked}
                        </div>
                        <div className="text-sm text-gray-500">
                          {withdrawal.accountHolder}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const accountNumber = withdrawal.accountNumber || withdrawal.accountNumberMasked;
                          copyToClipboard(accountNumber, "계좌번호");
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                        title="복사"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {withdrawal.requestedAt ? formatDate(withdrawal.requestedAt) : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WithdrawalsList;
